import { useState } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { getAccountingPermissions } from "@/lib/accounting/engine";
import { LedgerGroup, GROUP_NATURE, Ledger } from "@/lib/accounting/types";
import { diagnoseCompanyChart, DiagnosticReport } from "@/lib/accounting/diagnostics";
import { AccountingRepairDialog } from "@/components/accounting/AccountingRepairDialog";
import { EditLedgerDialog } from "@/components/accounting/EditLedgerDialog";
import { LedgerDetailDialog } from "@/components/accounting/LedgerDetailDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, BookOpen, Wrench, Pencil, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const GROUPS = Object.keys(GROUP_NATURE) as LedgerGroup[];

const LedgersPage = () => {
  const { ledgers, addLedger } = useAccounting();
  const { activeCompany } = useCompany();
  const { role } = useAuth();
  const { toast } = useToast();
  const perms = getAccountingPermissions(role, "Ledger");
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [diagReport, setDiagReport] = useState<DiagnosticReport | null>(null);
  const [showRepairModal, setShowRepairModal] = useState(false);

  // Edit and Detail modal state
  const [selectedLedgerForEdit, setSelectedLedgerForEdit] = useState<Ledger | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLedgerForDetail, setSelectedLedgerForDetail] = useState<Ledger | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [group, setGroup] = useState<LedgerGroup>("Sundry Debtors");
  const [openingBalance, setOpeningBalance] = useState(0);
  const [balanceType, setBalanceType] = useState<"Dr" | "Cr">("Dr");
  const [gstApplicable, setGstApplicable] = useState(false);
  const [gstin, setGstin] = useState("");

  const filtered = ledgers.filter((l) => {
    const matchesSearch = l.name.toLowerCase().includes(search.toLowerCase()) || l.group.toLowerCase().includes(search.toLowerCase());
    const matchesGroup = filterGroup === "all" || l.group === filterGroup;
    return matchesSearch && matchesGroup;
  });

  const groupedLedgers = filtered.reduce<Record<string, Ledger[]>>((acc, l) => {
    (acc[l.group] = acc[l.group] || []).push(l);
    return acc;
  }, {});

  const handleCreate = () => {
    if (!name.trim()) {
      toast({ title: "Error", description: "Ledger name is required", variant: "destructive" });
      return;
    }
    if (name.trim().length > 100) {
      toast({ title: "Error", description: "Ledger name must be under 100 characters", variant: "destructive" });
      return;
    }
    addLedger({ name: name.trim(), group, openingBalance, openingBalanceType: balanceType, gstApplicable, gstin: gstin.trim() || undefined, isSystem: false });
    toast({ title: "Ledger Created", description: `"${name}" added to ${group}` });
    setOpen(false);
    setName("");
    setOpeningBalance(0);
    setGstin("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Chart of Accounts</h1>
          <p className="text-sm text-muted-foreground">Manage ledgers and account groups ({ledgers.length} ledgers)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 gap-1.5"
            onClick={() => {
              const report = diagnoseCompanyChart(activeCompany, ledgers);
              setDiagReport(report);
              setShowRepairModal(true);
            }}
          >
            <Wrench className="h-4 w-4" />
            Diagnose & Repair
          </Button>

          {perms.canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent text-accent-foreground hover:bg-accent/90">
                  <Plus className="h-4 w-4 mr-2" /> New Ledger
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Create Ledger</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Ledger Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} className="w-full px-3 py-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent" placeholder="e.g. Wipro Ltd" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Account Group</label>
                  <Select value={group} onValueChange={(v) => setGroup(v as LedgerGroup)}>
                    <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GROUPS.map((g) => (
                        <SelectItem key={g} value={g} className="text-sm">{g} <span className="text-muted-foreground ml-1">({GROUP_NATURE[g]})</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Opening Balance</label>
                    <input type="number" min="0" value={openingBalance} onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Type</label>
                    <Select value={balanceType} onValueChange={(v) => setBalanceType(v as "Dr" | "Cr")}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dr">Debit (Dr)</SelectItem>
                        <SelectItem value="Cr">Credit (Cr)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="gst" checked={gstApplicable} onChange={(e) => setGstApplicable(e.target.checked)} className="accent-accent" />
                  <label htmlFor="gst" className="text-sm text-foreground">GST Applicable</label>
                </div>
                {gstApplicable && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">GSTIN</label>
                    <input value={gstin} onChange={(e) => setGstin(e.target.value)} maxLength={15} className="w-full px-3 py-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent" placeholder="27AABCU9603R1ZM" />
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreate} className="bg-accent text-accent-foreground hover:bg-accent/90">Create</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2 flex-1 max-w-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ledgers..." className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground" />
        </div>
        <Select value={filterGroup} onValueChange={setFilterGroup}>
          <SelectTrigger className="w-[200px] h-9 text-sm"><SelectValue placeholder="All Groups" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            {GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {Object.entries(groupedLedgers).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, items]) => (
          <div key={groupName} className="rounded-lg bg-card border border-border shadow-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b border-border">
              <BookOpen className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-foreground">{groupName}</span>
              <span className="text-xs text-muted-foreground ml-1">({items.length})</span>
              <span className="ml-auto text-xs px-2 py-0.5 rounded bg-accent/10 text-accent font-medium">{GROUP_NATURE[groupName as LedgerGroup]}</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs">Name</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs">Opening Balance</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs">Current Balance</th>
                  <th className="text-center py-2 px-4 font-medium text-muted-foreground text-xs">GST</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-t border-border/30 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-4 text-foreground font-medium">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLedgerForDetail(l);
                          setDetailDialogOpen(true);
                        }}
                        className="text-left font-medium text-foreground hover:text-blue-600 transition-colors focus:outline-none focus:underline"
                        title="Click to view details and statement"
                      >
                        {l.name}
                      </button>
                      {l.isSystem && <span className="text-[10px] text-muted-foreground ml-1">(System)</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-muted-foreground">₹{l.openingBalance.toLocaleString("en-IN")} {l.openingBalanceType}</td>
                    <td className="py-2.5 px-4 text-right font-semibold text-foreground">₹{l.currentBalance.toLocaleString("en-IN")} {l.currentBalanceType}</td>
                    <td className="py-2.5 px-4 text-center">{l.gstApplicable ? <span className="text-xs text-success">✓</span> : <span className="text-xs text-muted-foreground">—</span>}</td>
                    <td className="py-2 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedLedgerForDetail(l);
                            setDetailDialogOpen(true);
                          }}
                          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                          title="View Account Details & Statement"
                          aria-label={`View details of ${l.name}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>

                        {perms.canEdit && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedLedgerForEdit(l);
                              setEditDialogOpen(true);
                            }}
                            className="h-8 px-2 text-xs gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-transparent hover:border-blue-200 font-semibold"
                            title={`Edit ledger "${l.name}"`}
                            aria-label={`Edit ${l.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <AccountingRepairDialog
        open={showRepairModal}
        onOpenChange={setShowRepairModal}
        report={diagReport}
      />

      <EditLedgerDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        ledger={selectedLedgerForEdit}
        onSaved={(updated) => {
          if (selectedLedgerForDetail?.id === updated.id) {
            setSelectedLedgerForDetail(updated);
          }
        }}
      />

      <LedgerDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        ledger={selectedLedgerForDetail}
        onEditClick={(ledgerToEdit) => {
          setSelectedLedgerForEdit(ledgerToEdit);
          setEditDialogOpen(true);
        }}
        canEdit={perms.canEdit}
      />
    </div>
  );
};

export default LedgersPage;
